import { useState, useRef, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { aiSearch, getAIConsultantVideos, getAIToolsStatus, getMyCredits } from '../lib/api';
import {
  Send, Sparkles, User, Copy, Check, RotateCcw, Play, ChevronDown,
  Loader2, MessageSquare, X, Minus, Plus, BotMessageSquare, Coins, Maximize2
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';

const AI_CONSULTANT_ICON = "https://static.prod-images.emergentagent.com/jobs/cedee861-1d2e-4012-91c4-57a04e6b2415/images/1301e85db0f0fc2729ff63b21033bb734b2a1ed13c96fb36cc9aaba97439f25f.png";

const SUGGESTED_PROMPTS = [
  { text: "What ERP modules work best for manufacturing?", icon: "wrench" },
  { text: "How does ABSOLIN handle GST compliance?", icon: "receipt" },
  { text: "Tell me about your inventory management", icon: "box" },
  { text: "How long does ERP implementation take?", icon: "clock" },
];

export default function AIConsultantSidebar() {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [query, setQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [copiedId, setCopiedId] = useState(null);
  const [videos, setVideos] = useState([]);
  const [sessionId, setSessionId] = useState('');
  const [isDisabled, setIsDisabled] = useState(false);
  const [credits, setCredits] = useState(null);
  const [chatHistory, setChatHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const textareaRef = useRef(null);

  // Reactively check auth token
  const [token, setToken] = useState(localStorage.getItem('auth_token'));
  useEffect(() => {
    const checkToken = () => setToken(localStorage.getItem('auth_token'));
    // Listen for storage changes and check on focus
    window.addEventListener('storage', checkToken);
    window.addEventListener('focus', checkToken);
    // Also poll every 2 seconds for same-tab login
    const interval = setInterval(checkToken, 2000);
    return () => { window.removeEventListener('storage', checkToken); window.removeEventListener('focus', checkToken); clearInterval(interval); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load status & videos on mount
  useEffect(() => {
    if (!token) return;
    const init = async () => {
      try { const res = await getAIToolsStatus(); setIsDisabled(!res.data.ai_consultant_enabled); } catch { setIsDisabled(false); }
      try { const res = await getAIConsultantVideos(); setVideos(res.data || []); } catch {}
      try { const res = await getMyCredits(); setCredits(res.data.credits); } catch {}
      // Load chat history from localStorage
      const saved = localStorage.getItem('ai_chat_history');
      if (saved) setChatHistory(JSON.parse(saved));
    };
    init();
  }, [token]);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);

  // Auto-resize textarea
  useEffect(() => {
    const ta = textareaRef.current;
    if (ta) { ta.style.height = 'auto'; ta.style.height = `${Math.min(ta.scrollHeight, 120)}px`; }
  }, [query]);

  const handleSend = async (customQuery = null) => {
    const messageText = customQuery || query.trim();
    if (!messageText || isLoading) return;
    setQuery('');

    const userMessage = { id: Date.now(), role: 'user', content: messageText, timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);

    try {
      const conversationHistory = messages.map(m => ({ role: m.role === 'assistant' ? 'assistant' : 'user', content: m.content }));
      const response = await aiSearch(messageText, conversationHistory, sessionId, null, 'en');
      const { answer, sources, suggested_problems, suggested_videos, session_id: returnedSessionId } = response.data;

      if (returnedSessionId && !sessionId) setSessionId(returnedSessionId);

      const assistantMessage = {
        id: Date.now() + 1, role: 'assistant', content: answer,
        sources: sources || [], suggestedProblems: suggested_problems || [],
        suggestedVideos: suggested_videos || videos.slice(0, 2),
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, assistantMessage]);
      try { const cr = await getMyCredits(); setCredits(cr.data.credits); } catch {}
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to get response';
      const isCredits = errorMsg.includes("credits") || errorMsg.includes("No credits");
      toast.error(isCredits ? "No credits remaining. Contact your admin." : errorMsg);
      setMessages(prev => [...prev, {
        id: Date.now() + 1, role: 'assistant', isError: true, timestamp: new Date().toISOString(),
        content: isCredits ? "You've run out of AI credits. Please contact your administrator." : "I'm having trouble processing your request. Please try again."
      }]);
    } finally { setIsLoading(false); }
  };

  const handleKeyDown = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } };
  const handleCopy = async (text, id) => { await navigator.clipboard.writeText(text); setCopiedId(id); setTimeout(() => setCopiedId(null), 2000); };
  const handleRegenerate = (idx) => { const um = messages[idx - 1]; if (um?.role === 'user') { setMessages(prev => prev.slice(0, idx)); handleSend(um.content); } };

  const handleNewChat = () => {
    // Save current chat to history
    if (messages.length > 0) {
      const firstMsg = messages.find(m => m.role === 'user');
      const chatEntry = {
        id: sessionId || Date.now().toString(),
        title: firstMsg ? firstMsg.content.slice(0, 50) + (firstMsg.content.length > 50 ? '...' : '') : 'New Chat',
        messages: [...messages],
        date: new Date().toISOString()
      };
      const updated = [chatEntry, ...chatHistory].slice(0, 20);
      setChatHistory(updated);
      localStorage.setItem('ai_chat_history', JSON.stringify(updated));
    }
    setMessages([]);
    setQuery('');
    setSessionId('');
    setShowHistory(false);
  };

  const loadChat = (chat) => {
    setMessages(chat.messages);
    setSessionId(chat.id);
    setShowHistory(false);
  };

  const toggleSidebar = () => {
    if (isMinimized) { setIsMinimized(false); setIsOpen(true); }
    else if (isOpen) { setIsOpen(false); }
    else { setIsOpen(true); }
  };

  const minimize = () => { setIsMinimized(true); setIsOpen(false); };

  // Group history by date
  const groupedHistory = chatHistory.reduce((acc, chat) => {
    const d = new Date(chat.date);
    const today = new Date();
    let label;
    if (d.toDateString() === today.toDateString()) label = 'Today';
    else if (d.toDateString() === new Date(today - 86400000).toDateString()) label = 'Yesterday';
    else label = d.toLocaleDateString();
    if (!acc[label]) acc[label] = [];
    acc[label].push(chat);
    return acc;
  }, {});

  if (isDisabled) return null;

  // Hide on login/auth pages and Collection Management screens
  const hiddenPaths = ['/login', '/forgot-password', '/reset-password'];
  const hiddenPrefixes = ['/collections', '/collection-planning'];
  if (hiddenPaths.includes(location.pathname)) return null;
  if (hiddenPrefixes.some(p => location.pathname === p || location.pathname.startsWith(`${p}/`))) return null;

  // Show request access form for unauthenticated users
  if (!token) {
    return (
      <>
        <button onClick={() => setIsOpen(!isOpen)}
          className="fixed bottom-16 right-4 z-50 w-12 h-12 sm:w-auto sm:h-auto sm:bottom-8 sm:right-6 sm:px-4 sm:py-3 rounded-full sm:rounded-full bg-gradient-to-r from-[#0066CC] to-[#0052A3] text-white shadow-lg hover:shadow-xl hover:scale-105 transition-all flex items-center justify-center sm:gap-2"
          data-testid="ai-fab-public"
        >
          <img src={AI_CONSULTANT_ICON} alt="AI" className="w-6 h-6 object-contain" />
          <span className="text-sm font-medium hidden sm:inline">AI Consultant</span>
        </button>

        {isOpen && (
          <div className="fixed bottom-[5.5rem] sm:bottom-24 right-4 sm:right-6 z-50 w-[calc(100vw-32px)] sm:w-[340px] max-w-[340px] bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden" data-testid="ai-request-access-panel">
            <div className="bg-gradient-to-r from-[#0066CC] to-[#0052A3] px-5 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <BotMessageSquare className="w-5 h-5 text-white" />
                  <span className="text-white font-semibold text-sm">AI Consultant</span>
                </div>
                <button onClick={() => setIsOpen(false)} className="text-white/70 hover:text-white">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <p className="text-blue-100 text-xs mt-1">Get instant answers about ERP solutions</p>
            </div>
            <div className="p-5 text-center">
              <div className="w-14 h-14 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-3">
                <BotMessageSquare className="w-7 h-7 text-[#0066CC]" />
              </div>
              <h3 className="font-bold text-slate-900 text-base mb-1">Unlock AI Consultant</h3>
              <p className="text-xs text-slate-500 mb-4 leading-relaxed">
                Our AI Consultant can answer all your questions about ERP solutions, industry challenges, and digital transformation.
              </p>
              <p className="text-xs text-slate-400 mb-4">
                Please contact our sales team to get login credentials and full access.
              </p>
              <a href="mailto:info@absolinsoft.com">
                <button className="w-full py-2.5 bg-[#0066CC] hover:bg-[#0052A3] text-white rounded-lg text-sm font-semibold transition-colors">
                  Contact Sales Team
                </button>
              </a>
              <a href="tel:+918297555678" className="block mt-2 text-xs text-[#0066CC] hover:underline font-medium">
                or call +91 829 755 5678
              </a>
            </div>
          </div>
        )}
      </>
    );
  }

  // Get last assistant message for minimized view
  const lastAssistantMsg = [...messages].reverse().find(m => m.role === 'assistant' && !m.isError);

  return (
    <>
      {/* Floating Button - hidden when minimized or sidebar open */}
      {!isMinimized && (
        <button onClick={toggleSidebar}
          className={`fixed bottom-16 right-4 sm:bottom-8 sm:right-6 z-[9998] flex items-center justify-center gap-2.5 w-12 h-12 sm:w-auto sm:h-auto sm:px-4 sm:py-3 rounded-full sm:rounded-2xl shadow-xl transition-all duration-300 hover:scale-105 active:scale-95 ${
            isOpen ? 'bg-slate-700 text-white' : 'bg-[#0066CC] text-white shadow-[#0066CC]/30 hover:shadow-[#0066CC]/50'
          }`}
          data-testid="ai-consultant-fab"
        >
          {isOpen ? <X className="w-5 h-5" /> : (
            <>
              <img src={AI_CONSULTANT_ICON} alt="" className="w-6 h-6 object-contain" />
              <span className="text-sm font-semibold hidden sm:inline">AI Consultant</span>
            </>
          )}
        </button>
      )}

      {/* Minimized Floating Widget */}
      {isMinimized && !isOpen && (
        <div className="fixed bottom-16 right-4 sm:bottom-8 sm:right-6 z-[9999] w-[380px] max-w-[calc(100vw-32px)] rounded-2xl shadow-2xl overflow-hidden border border-slate-700/50"
          style={{ animation: 'slideUp 0.3s ease-out' }}
          data-testid="ai-consultant-minimized">
          <style>{`@keyframes slideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }`}</style>
          {/* Mini Header */}
          <div className="flex items-center justify-between px-3.5 py-2.5 bg-[#1a1a2e]">
            <div className="flex items-center gap-2">
              <img src={AI_CONSULTANT_ICON} alt="" className="w-5 h-5 object-contain" />
              <span className="text-[13px] font-semibold text-white truncate max-w-[180px]">
                {messages.length > 0 ? 'AI Consultant' : 'New chat'}
              </span>
            </div>
            <div className="flex items-center gap-0.5">
              <button onClick={handleNewChat} className="p-1.5 hover:bg-white/10 rounded-md text-slate-400 hover:text-white transition-colors" title="New Chat">
                <Plus className="w-3.5 h-3.5" />
              </button>
              <button onClick={() => { setIsMinimized(false); setIsOpen(true); }} className="p-1.5 hover:bg-white/10 rounded-md text-slate-400 hover:text-white transition-colors" title="Expand" data-testid="minimized-expand">
                <Maximize2 className="w-3.5 h-3.5" />
              </button>
              <button onClick={() => setIsMinimized(false)} className="p-1.5 hover:bg-white/10 rounded-md text-slate-400 hover:text-white transition-colors" title="Close" data-testid="minimized-close">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          {/* Mini Content */}
          <div className="bg-[#16162a] max-h-[280px] overflow-y-auto px-3.5 py-3 scrollbar-thin" style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(255,255,255,0.15) transparent' }}>
            {lastAssistantMsg ? (
              <div className="text-[13px] text-slate-300 leading-relaxed">
                <ReactMarkdown components={{
                  p: ({ children }) => <p className="mb-2 text-[13px] text-slate-300">{children}</p>,
                  ul: ({ children }) => <ul className="mb-2 space-y-1 ml-3">{children}</ul>,
                  li: ({ children }) => <li className="text-[13px] text-slate-300 list-disc">{children}</li>,
                  strong: ({ children }) => <strong className="text-white font-semibold">{children}</strong>,
                  a: ({ href, children }) => <a href={href} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">{children}</a>,
                }}>{lastAssistantMsg.content}</ReactMarkdown>
              </div>
            ) : (
              <p className="text-[13px] text-slate-500 text-center py-4">Ask me about ERP solutions & digital transformation</p>
            )}
          </div>

          {/* Mini Input */}
          <div className="bg-[#1a1a2e] border-t border-white/5 p-2.5">
            <div className="relative">
              <input value={query} onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                placeholder="Enter response..." disabled={isLoading}
                className="w-full bg-[#252540] text-white placeholder:text-slate-500 rounded-xl px-3.5 py-2.5 pr-10 text-sm border border-white/5 focus:outline-none focus:border-blue-500/30"
                data-testid="minimized-input" />
              <button onClick={() => handleSend()} disabled={isLoading || !query.trim()}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 bg-[#0066CC] hover:bg-[#0055aa] disabled:bg-slate-600 disabled:text-slate-500 text-white rounded-lg flex items-center justify-center transition-colors">
                {isLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sidebar Panel */}
      <div className={`fixed top-0 right-0 h-full z-[9999] transition-transform duration-300 ease-out ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}
        style={{ width: 'min(420px, 100vw)' }}>
        <div className="h-full flex flex-col bg-white border-l border-slate-200 shadow-2xl" data-testid="ai-consultant-sidebar">

          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-white">
            <div className="flex items-center gap-2 relative">
              <button onClick={() => setShowHistory(!showHistory)} className="flex items-center gap-2 hover:bg-slate-50 rounded-lg px-2 py-1.5 transition-colors" data-testid="sidebar-title-btn">
                <img src={AI_CONSULTANT_ICON} alt="" className="w-6 h-6 object-contain" />
                <span className="text-sm font-semibold text-slate-800">{messages.length > 0 ? 'AI Consultant' : 'New chat'}</span>
                <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform ${showHistory ? 'rotate-180' : ''}`} />
              </button>

              {/* History Dropdown */}
              {showHistory && (
                <div className="absolute top-full left-0 mt-1 w-72 bg-white border border-slate-200 rounded-xl shadow-xl z-50 max-h-[400px] overflow-y-auto" data-testid="chat-history-dropdown">
                  <div className="p-2">
                    {Object.keys(groupedHistory).length === 0 ? (
                      <p className="text-xs text-slate-400 text-center py-4">No chat history</p>
                    ) : (
                      Object.entries(groupedHistory).map(([label, chats]) => (
                        <div key={label}>
                          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider px-2 py-1.5">{label}</p>
                          {chats.map((chat) => (
                            <button key={chat.id} onClick={() => loadChat(chat)}
                              className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 rounded-lg truncate transition-colors">
                              <MessageSquare className="w-3.5 h-3.5 inline mr-2 text-slate-400" />{chat.title}
                            </button>
                          ))}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center gap-1">
              <button onClick={handleNewChat} className="p-2 hover:bg-slate-100 rounded-lg text-slate-400 hover:text-slate-600 transition-colors" title="New Chat" data-testid="sidebar-new-chat">
                <Plus className="w-4 h-4" />
              </button>
              <button onClick={minimize} className="p-2 hover:bg-slate-100 rounded-lg text-slate-400 hover:text-slate-600 transition-colors" title="Minimize" data-testid="sidebar-minimize">
                <Minus className="w-4 h-4" />
              </button>
              <button onClick={() => setIsOpen(false)} className="p-2 hover:bg-slate-100 rounded-lg text-slate-400 hover:text-slate-600 transition-colors" title="Close" data-testid="sidebar-close">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div ref={messagesContainerRef} className="flex-1 overflow-y-auto">
            {messages.length === 0 ? (
              /* Welcome State */
              <div className="px-5 py-8">
                <div className="text-center mb-6">
                  <div className="w-14 h-14 bg-blue-50 rounded-2xl flex items-center justify-center mx-auto mb-4 p-2">
                    <img src={AI_CONSULTANT_ICON} alt="" className="w-10 h-10 object-contain" />
                  </div>
                  <h3 className="text-base font-semibold text-slate-800 mb-1">Hi, I'm your AI Consultant</h3>
                  <p className="text-xs text-slate-400 leading-relaxed max-w-[280px] mx-auto">
                    Ask me about ERP solutions, digital transformation, or any business challenge.
                  </p>
                </div>

                <div className="space-y-2" data-testid="suggested-prompts">
                  {SUGGESTED_PROMPTS.map((p, i) => (
                    <button key={i} onClick={() => handleSend(p.text)}
                      className="w-full text-left px-4 py-3 bg-slate-50 hover:bg-blue-50 border border-slate-100 hover:border-blue-200 rounded-xl text-sm text-slate-700 hover:text-[#0066CC] transition-all"
                      data-testid={`sidebar-prompt-${i}`}>
                      {p.text}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              /* Chat Messages */
              <div className="px-4 py-4 space-y-4">
                {messages.map((msg, idx) => (
                  <div key={msg.id}>
                    {msg.role === 'user' ? (
                      <div className="flex justify-end">
                        <div className="bg-[#0066CC] text-white px-4 py-2.5 rounded-2xl rounded-tr-md max-w-[85%]">
                          <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-start gap-2.5">
                        <div className="w-7 h-7 bg-blue-50 rounded-lg flex items-center justify-center shrink-0 p-1 mt-0.5">
                          <img src={AI_CONSULTANT_ICON} alt="" className="w-5 h-5 object-contain" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className={`sidebar-ai-content text-sm ${msg.isError ? 'text-red-600' : ''}`}>
                            <ReactMarkdown components={{
                              p: ({ children }) => <p className="mb-3 text-[13px] leading-relaxed text-slate-700">{children}</p>,
                              ul: ({ children }) => <ul className="mb-3 space-y-1.5 ml-1">{children}</ul>,
                              ol: ({ children }) => <ol className="mb-3 space-y-1.5 ml-1 list-decimal list-inside">{children}</ol>,
                              li: ({ children }) => <li className="text-[13px] leading-relaxed text-slate-700 flex items-start gap-1.5"><span className="text-blue-500 mt-1 shrink-0">•</span><span>{children}</span></li>,
                              strong: ({ children }) => <strong className="font-semibold text-slate-800">{children}</strong>,
                              h1: ({ children }) => <h1 className="text-base font-bold text-slate-900 mt-4 mb-2">{children}</h1>,
                              h2: ({ children }) => <h2 className="text-sm font-bold text-slate-900 mt-3 mb-2">{children}</h2>,
                              h3: ({ children }) => <h3 className="text-sm font-semibold text-slate-900 mt-2 mb-1">{children}</h3>,
                              code: ({ children, inline }) => inline
                                ? <code className="px-1 py-0.5 bg-slate-100 rounded text-xs font-mono">{children}</code>
                                : <code className="block p-2 bg-slate-100 rounded-lg text-xs font-mono my-2 overflow-x-auto">{children}</code>,
                              blockquote: ({ children }) => <blockquote className="border-l-3 border-blue-400 pl-3 py-1 my-2 bg-blue-50/50 rounded-r italic text-slate-600 text-xs">{children}</blockquote>,
                              a: ({ href, children }) => <a href={href} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{children}</a>,
                            }}>{msg.content}</ReactMarkdown>
                          </div>

                          {/* Suggested Videos */}
                          {msg.suggestedVideos?.length > 0 && (
                            <div className="mt-2 space-y-1.5">
                              {msg.suggestedVideos.slice(0, 2).map((v, vi) => (
                                <a key={vi} href={v.youtube_url} target="_blank" rel="noopener noreferrer"
                                  className="flex items-center gap-2 p-2 bg-slate-50 hover:bg-slate-100 rounded-lg text-xs text-slate-600 transition-colors">
                                  <Play className="w-3.5 h-3.5 text-red-500 shrink-0" /><span className="truncate">{v.title}</span>
                                </a>
                              ))}
                            </div>
                          )}

                          {/* Actions */}
                          <div className="mt-1.5 flex items-center gap-0.5">
                            <button onClick={() => handleCopy(msg.content, msg.id)} className="p-1.5 text-slate-300 hover:text-slate-500 transition-colors">
                              {copiedId === msg.id ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
                            </button>
                            <button onClick={() => handleRegenerate(idx)} className="p-1.5 text-slate-300 hover:text-slate-500 transition-colors">
                              <RotateCcw className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}

                {/* Loading */}
                {isLoading && (
                  <div className="flex items-start gap-2.5">
                    <div className="w-7 h-7 bg-blue-50 rounded-lg flex items-center justify-center p-1">
                      <img src={AI_CONSULTANT_ICON} alt="" className="w-5 h-5 object-contain" />
                    </div>
                    <div className="flex items-center gap-1.5 py-2">
                      <div className="flex gap-1">
                        <div className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                        <div className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                        <div className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                      <span className="text-xs text-slate-400">Thinking...</span>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>

          {/* Input Area */}
          <div className="border-t border-slate-100 p-3 bg-white">
            <div className="relative bg-slate-50 border border-slate-200 rounded-xl focus-within:border-[#0066CC] focus-within:ring-2 focus-within:ring-blue-100 transition-all">
              <textarea ref={textareaRef} value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={handleKeyDown}
                placeholder="Ask about ERP solutions..." disabled={isLoading} rows={1}
                className="w-full px-3.5 py-2.5 pr-11 bg-transparent resize-none focus:outline-none text-slate-800 placeholder:text-slate-400 text-sm max-h-[120px]"
                data-testid="sidebar-chat-input" />
              <button onClick={() => handleSend()} disabled={isLoading || !query.trim()}
                className="absolute right-2 bottom-2 w-7 h-7 bg-[#0066CC] hover:bg-[#0055aa] disabled:bg-slate-200 disabled:text-slate-400 text-white rounded-lg flex items-center justify-center transition-colors"
                data-testid="sidebar-send-btn">
                {isLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              </button>
            </div>
            <div className="flex items-center justify-between mt-1.5 px-1">
              <p className="text-[10px] text-slate-400">Powered by ABSOLIN Knowledge Base</p>
              {credits !== null && (
                <span className={`text-[10px] flex items-center gap-1 ${credits <= 5 ? "text-red-500" : credits <= 20 ? "text-amber-500" : "text-slate-400"}`} data-testid="sidebar-credits">
                  <Coins className="w-3 h-3" /> {credits} credits
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Backdrop on mobile */}
      {isOpen && (
        <div className="fixed inset-0 bg-black/20 z-[997] sm:hidden" onClick={() => setIsOpen(false)} />
      )}
    </>
  );
}
